import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Fade, Grid, Card, CardContent,
  CircularProgress, Alert, TextField, Button, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Stack, Divider,
} from '@mui/material';
import {
  BarChart as BarChartIcon, Download, Refresh, BoltOutlined,
  AttachMoney, EventNote, Warning,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getDashboardData } from '../../services/dashboardService';

// ── Paleta de colores por ubicación ────────────────────────────────────────────
const LOCATION_COLORS = [
  '#2ECC71', '#3498DB', '#E67E22', '#9B59B6',
  '#1ABC9C', '#E74C3C', '#F39C12', '#34495E',
];

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n, dec = 2) =>
  n == null ? '—' : Number(n).toLocaleString('es-CO', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtCOP = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;

function buildChartData(monthly_summary) {
  if (!monthly_summary?.length) return { kwhData: [], incomeData: [], locations: [] };

  const locSet = new Set();
  monthly_summary.forEach(m => m.by_location?.forEach(l => locSet.add(l.location)));
  const locations = [...locSet];

  const kwhData = monthly_summary.map(m => {
    const row = { month: m.month_label };
    const locMap = Object.fromEntries((m.by_location || []).map(l => [l.location, l]));
    locations.forEach(loc => { row[loc] = locMap[loc]?.kwh ?? 0; });
    return row;
  });

  const incomeData = monthly_summary.map(m => {
    const row = { month: m.month_label };
    const locMap = Object.fromEntries((m.by_location || []).map(l => [l.location, l]));
    locations.forEach(loc => { row[loc] = locMap[loc]?.income ?? 0; });
    return row;
  });

  return { kwhData, incomeData, locations };
}

function exportToExcel(monthly_summary, totals) {
  const wb = XLSX.utils.book_new();

  // Hoja 1 – Resumen Mensual
  const summaryRows = [
    ['Mes', 'Reservas', 'kWh ajustado', 'kWh original', 'Ingresos COP',
     'Prom kWh/reserva', 'Prom ingreso/reserva', 'Ceros corregidos', 'Capeados'],
    ...monthly_summary.map(m => [
      m.month_label, m.bookings_count,
      m.kwh_total, m.kwh_raw_total, m.income_total,
      m.kwh_avg, m.income_avg, m.zeros_corrected, m.capped,
    ]),
    [],
    ['TOTALES', totals.bookings, totals.kwh_total, '', totals.income_total,
     '', '', totals.zeros_corrected, totals.capped],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
  ws1['!cols'] = [14, 10, 14, 14, 16, 18, 20, 18, 10].map(w => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws1, 'Resumen Mensual');

  // Hoja 2 – Por Ubicación
  const locHeaders = ['Mes'];
  const locSet = new Set();
  monthly_summary.forEach(m => m.by_location?.forEach(l => locSet.add(l.location)));
  const locations = [...locSet];
  locHeaders.push(...locations.map(l => `kWh ${l}`), ...locations.map(l => `Ingresos ${l}`));
  const locRows = [locHeaders, ...monthly_summary.map(m => {
    const locMap = Object.fromEntries((m.by_location || []).map(l => [l.location, l]));
    return [m.month_label, ...locations.map(l => locMap[l]?.kwh ?? 0), ...locations.map(l => locMap[l]?.income ?? 0)];
  })];
  const ws2 = XLSX.utils.aoa_to_sheet(locRows);
  XLSX.utils.book_append_sheet(wb, ws2, 'Por Ubicación');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'dashboard_consumo_ingresos.xlsx');
}

// ── KPI Card ───────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, color, subtitle }) {
  return (
    <Card sx={{ borderLeft: `4px solid ${color}`, height: '100%' }} elevation={1}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700} color="text.primary">{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Custom Tooltip Recharts ────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, minWidth: 160 }} elevation={3}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      {payload.map((p, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ color: p.fill }}>
          {p.name}: {unit === 'COP' ? fmtCOP(p.value) : fmt(p.value)} {unit !== 'COP' ? unit : ''}
        </Typography>
      ))}
    </Paper>
  );
}

// ── Chart section wrapper ──────────────────────────────────────────────────────
function ChartCard({ title, children, onDownload }) {
  return (
    <Card elevation={1}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={700} color="#002642">{title}</Typography>
          {onDownload && (
            <Tooltip title="Descargar PNG">
              <IconButton size="small" onClick={onDownload}><Download fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

// ── Correction chip ────────────────────────────────────────────────────────────
function CorrectionChip({ correction }) {
  if (correction === 'zero_filled')
    return <Chip label="Cero corregido" size="small" sx={{ bgcolor: '#FFF2CC', color: '#7D6608', fontWeight: 600 }} />;
  if (correction === 'capped')
    return <Chip label="Capeado" size="small" sx={{ bgcolor: '#FCE4D6', color: '#922B21', fontWeight: 600 }} />;
  return null;
}

// ── Main page ──────────────────────────────────────────────────────────────────
const DashboardPage = () => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate)   params.end_date   = endDate;
      const result = await getDashboardData(params);
      setData(result);
    } catch (e) {
      setError('Error al cargar el dashboard. Verifica la conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, []);

  const { monthly_summary = [], totals = {} } = data || {};
  const { kwhData, incomeData, locations } = buildChartData(monthly_summary);

  return (
    <Fade in timeout={600}>
      <Container maxWidth={false} sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 4 }, maxWidth: '1600px', mx: 'auto' }}>

        {/* ── Header ── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={3} gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1.5, borderRadius: 3, background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)', display: 'flex' }}>
              <BarChartIcon sx={{ color: '#fff', fontSize: '2rem' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="#002642" lineHeight={1.2}>
                Dashboard Consumo e Ingresos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                kWh ajustado · Ingresos mensuales · Correcciones automáticas
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <TextField
              label="Desde" type="date" size="small"
              value={startDate} onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 155 }}
            />
            <TextField
              label="Hasta" type="date" size="small"
              value={endDate} onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 155 }}
            />
            <Button variant="contained" onClick={load} startIcon={<Refresh />}
              sx={{ bgcolor: '#002642', '&:hover': { bgcolor: '#00577A' } }}>
              Actualizar
            </Button>
            <Button variant="outlined" startIcon={<Download />}
              disabled={!data || !monthly_summary.length}
              onClick={() => exportToExcel(monthly_summary, totals)}
              sx={{ borderColor: '#002642', color: '#002642' }}>
              Exportar Excel
            </Button>
          </Stack>
        </Stack>

        {/* ── Loading / Error ── */}
        {loading && <Box display="flex" justifyContent="center" my={6}><CircularProgress /></Box>}
        {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {data && !loading && (
          <>
            {/* ── KPI Cards ── */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiCard icon={<BoltOutlined />} label="kWh total (ajustado)"
                  value={fmt(totals.kwh_total)} color="#2ECC71"
                  subtitle="Con correcciones" />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiCard icon={<AttachMoney />} label="Ingresos totales"
                  value={fmtCOP(totals.income_total)} color="#3498DB"
                  subtitle="Débitos de billetera" />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiCard icon={<EventNote />} label="Reservas"
                  value={totals.bookings ?? '—'} color="#E67E22" />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiCard icon={<Warning sx={{ color: '#F39C12' }} />} label="Ceros corregidos"
                  value={totals.zeros_corrected ?? '—'} color="#F39C12"
                  subtitle="kWh=0 → mitad máx teórico" />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiCard icon={<Warning sx={{ color: '#E74C3C' }} />} label="Capeados"
                  value={totals.capped ?? '—'} color="#E74C3C"
                  subtitle=">1.95 kWh/h → 6.5 kWh/h" />
              </Grid>
            </Grid>

            {/* ── Charts ── */}
            <Grid container spacing={2} mb={3}>
              {/* Consumo mensual */}
              <Grid item xs={12} md={6}>
                <ChartCard title="Consumo Mensual por Ubicación (kWh)">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={kwhData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit=" kWh" width={70} />
                      <RTooltip content={<CustomTooltip unit="kWh" />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {locations.map((loc, i) => (
                        <Bar key={loc} dataKey={loc} stackId="a"
                          fill={LOCATION_COLORS[i % LOCATION_COLORS.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Ingresos mensuales */}
              <Grid item xs={12} md={6}>
                <ChartCard title="Ingresos Mensuales por Ubicación (COP)">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={incomeData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} width={70} />
                      <RTooltip content={<CustomTooltip unit="COP" />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {locations.map((loc, i) => (
                        <Bar key={loc} dataKey={loc} stackId="b"
                          fill={LOCATION_COLORS[i % LOCATION_COLORS.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            {/* ── Tabla Resumen Mensual ── */}
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight={700} color="#002642">
                    Resumen Mensual
                  </Typography>
                  <Button size="small" startIcon={<Download />} variant="outlined"
                    onClick={() => exportToExcel(monthly_summary, totals)}
                    sx={{ borderColor: '#002642', color: '#002642' }}>
                    Exportar tabla
                  </Button>
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#1F4E79', color: '#fff', fontWeight: 700, fontSize: '0.75rem' } }}>
                        <TableCell>Mes</TableCell>
                        <TableCell align="right">Reservas</TableCell>
                        <TableCell align="right">kWh ajustado</TableCell>
                        <TableCell align="right">kWh original</TableCell>
                        <TableCell align="right">Ingresos</TableCell>
                        <TableCell align="right">Prom kWh/res.</TableCell>
                        <TableCell align="right">Prom ingreso/res.</TableCell>
                        <TableCell align="center">Correcciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthly_summary.map((m, idx) => (
                        <TableRow key={m.year_month}
                          sx={{ bgcolor: idx % 2 === 0 ? '#EBF3FB' : 'white', '&:hover': { bgcolor: '#d6e9f8' } }}>
                          <TableCell sx={{ fontWeight: 600 }}>{m.month_label}</TableCell>
                          <TableCell align="right">{m.bookings_count}</TableCell>
                          <TableCell align="right">{fmt(m.kwh_total)}</TableCell>
                          <TableCell align="right" sx={{ color: '#888' }}>{fmt(m.kwh_raw_total)}</TableCell>
                          <TableCell align="right">{fmtCOP(m.income_total)}</TableCell>
                          <TableCell align="right">{fmt(m.kwh_avg)}</TableCell>
                          <TableCell align="right">{fmtCOP(m.income_avg)}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                              {m.zeros_corrected > 0 && (
                                <Chip label={`${m.zeros_corrected} ceros`} size="small"
                                  sx={{ bgcolor: '#FFF2CC', color: '#7D6608', fontWeight: 600, fontSize: '0.65rem' }} />
                              )}
                              {m.capped > 0 && (
                                <Chip label={`${m.capped} cap`} size="small"
                                  sx={{ bgcolor: '#FCE4D6', color: '#922B21', fontWeight: 600, fontSize: '0.65rem' }} />
                              )}
                              {m.zeros_corrected === 0 && m.capped === 0 && (
                                <Typography variant="caption" color="success.main">✓</Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Fila totales */}
                      <TableRow sx={{ bgcolor: '#1F4E79', '& td': { color: '#fff', fontWeight: 700 } }}>
                        <TableCell>TOTAL</TableCell>
                        <TableCell align="right">{totals.bookings}</TableCell>
                        <TableCell align="right">{fmt(totals.kwh_total)}</TableCell>
                        <TableCell align="right">—</TableCell>
                        <TableCell align="right">{fmtCOP(totals.income_total)}</TableCell>
                        <TableCell align="right">—</TableCell>
                        <TableCell align="right">—</TableCell>
                        <TableCell align="center">
                          {totals.zeros_corrected}↑ · {totals.capped}⚠
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* ── Leyenda correcciones ── */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" fontWeight={700} display="block" mb={1} color="#002642">
                Reglas de corrección aplicadas
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                <Chip label="🟡 kWh = 0 → mitad del máximo teórico (6.5 / 2 × horas)"
                  size="small" sx={{ bgcolor: '#FFF2CC', color: '#7D6608', fontWeight: 500 }} />
                <Chip label="🟠 kWh/hora > 1.95 → capeado a 6.5 kWh/hora"
                  size="small" sx={{ bgcolor: '#FCE4D6', color: '#922B21', fontWeight: 500 }} />
              </Stack>
            </Paper>
          </>
        )}

        {data && !loading && !monthly_summary.length && (
          <Alert severity="info">No hay reservas completadas en el período seleccionado.</Alert>
        )}
      </Container>
    </Fade>
  );
};

export default DashboardPage;
